import { Box, Button, Chip, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import PauseCircleFilledIcon from '@mui/icons-material/PauseCircleFilled';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DownloadIcon from '@mui/icons-material/Download'
import PaymentIcon from '@mui/icons-material/Payment'

import { EmiData, EmiItem, StatusType } from './LoanDetail';
import { Dispatch, SetStateAction } from 'react';
import { getLocalValue, LoacalVariables } from '../../commonFunctions/CommonFunctions';
import { InfoOutlined, SvgIconComponent, Warning } from '@mui/icons-material';


interface EmiSchedulesProps {
    loanData: any;
    emiData: EmiData;
    page: number;
    rowsPerPage: number;
    selectedEmis: any[];
    payingBulk: boolean;
    setSelectedEmis: Dispatch<SetStateAction<any[]>>
    setPage: Dispatch<SetStateAction<number>>;        // Correct type for page setter
    setRowsPerPage: Dispatch<SetStateAction<number>>;  // Correct type for rowsPerPage setter
    handleDownload: (format: 'pdf' | 'excel') => void;
    handleRepaymentClick: (item: any) => void;
    // handleCorrectionClick: (item: any) => void;
    handleBulkRepayment: (selectedItems: any[]) => void;
}

const statusConfig: Record<
    StatusType,
    { label: string; color: 'success' | 'warning' | 'primary' | 'error'; icon: SvgIconComponent }
> = {
    Paid: { label: 'Paid', color: 'success', icon: CheckCircleIcon },
    Partial: { label: 'Partial', color: 'warning', icon: HourglassBottomIcon },
    Overdue: { label: 'Overdue', color: 'warning', icon: WarningAmberIcon },
    Unpaid: { label: 'Unpaid', color: 'error', icon: ErrorIcon },
    Moratorium: { label: 'Moratorium', color: 'primary', icon: PauseCircleFilledIcon },
};

interface StatusChipProps {
    status: StatusType;
}

const EmiSchedules: React.FC<EmiSchedulesProps> = ({ loanData, emiData, page, rowsPerPage, setPage, setRowsPerPage, handleDownload, handleRepaymentClick }) => {
    const userType = getLocalValue(LoacalVariables.UserType);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Format currency values
    const formatCurrency = (value: number) => {
        return `₹${Number(value || 0).toLocaleString('en-IN')}`;
    };

    // Summary cards for top of component
    const SummaryCard = ({ title, value, color = 'primary' }: { title: string, value: number, color: string }) => (
        <Paper
            elevation={1}
            sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: `${color}.light`,
                color: `${color}.contrastText`,
                flex: 1,
                minWidth: isSmallMobile ? '100%' : 'auto'
            }}
        >
            <Typography variant="subtitle2" color={'white'} gutterBottom>
                {title}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color={'white'}>
                {formatCurrency(value)}
            </Typography>
        </Paper>
    );

    // Status chip component
    const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
        const config = statusConfig[status];
        const Icon = config.icon;

        return (
            <Chip
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Icon
                            fontSize="small"
                            sx={{ mr: 0.5 }}
                            color={config.color === 'error' ? 'error' : undefined}
                        />
                        <Typography variant="caption">{config.label}</Typography>
                    </Box>
                }
                color={config.color}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500 }}
            />
        );
    };

    return (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PaymentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" component="h2">
                    EMI Schedule
                </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Summary Cards */}
            <Box sx={{
                display: 'flex',
                flexDirection: isSmallMobile ? 'column' : 'row',
                gap: 2,
                mb: 3,
                flexWrap: 'wrap'
            }}>
                <SummaryCard title="Monthly EMI" value={emiData.emi} color="primary" />
                <SummaryCard title="Total Payment" value={emiData.totalPayment} color="secondary" />
                <SummaryCard title="Total Interest" value={emiData.totalInterest} color="info" />

                {emiData.outstandingBalance !== undefined && (
                    <SummaryCard
                        title="Outstanding Balance"
                        value={emiData.outstandingBalance}
                        color="warning"
                    />
                )}
                {emiData.totalRepayments !== undefined && (
                    <SummaryCard
                        title="Total Repaid"
                        value={emiData.totalRepayments}
                        color="success"
                    />
                )}
            </Box>

            {emiData.schedule.length > 0 ? (
                <>
                    {/* Desktop Table */}
                    {!isMobile ? (
                        <Table sx={{ mt: 3 }} stickyHeader>
                            <TableHead>
                                <Tooltip title="Repaid: EMI portion allocated | Actual Paid: Total payment including prepayments">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2"><strong>Actual Paid</strong></Typography>
                                        <InfoOutlined fontSize="small" color="action" />
                                    </Box>
                                </Tooltip>
                                <TableRow>
                                    <TableCell><strong>Month</strong></TableCell>
                                    <TableCell><strong>Due Date</strong></TableCell>
                                    <TableCell><strong>EMI (₹)</strong></TableCell>
                                    <TableCell><strong>Principal</strong></TableCell>
                                    <TableCell><strong>Interest</strong></TableCell>
                                    <TableCell><strong>Balance</strong></TableCell>
                                    <TableCell><strong>Repaid</strong></TableCell>
                                    {/* <TableCell><strong>Actual Paid</strong></TableCell> */}
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Action</strong></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {emiData.schedule
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((item: EmiItem, idx: number) => {
                                        const today = new Date();
                                        const isCurrent = new Date(item.dueDate).getMonth() === today.getMonth() &&
                                            new Date(item.dueDate).getFullYear() === today.getFullYear();
                                        const isMoratorium = item.isMoratoriumPeriod || false;

                                        return (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    backgroundColor: isCurrent ? '#ffe1e1' :
                                                        isMoratorium ? '#f5f5f5' :
                                                            idx % 2 === 0 ? 'grey.50' : 'white',
                                                    opacity: isMoratorium ? 0.7 : 1,
                                                    '&:hover': { backgroundColor: isMoratorium ? '#f5f5f5' : 'grey.100' },
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {item.month}
                                                        {isMoratorium && (
                                                            <Chip
                                                                label="Moratorium"
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                <TableCell>{new Date(item.dueDate).toLocaleDateString()}</TableCell>

                                                {/* EMI Amount */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            sx={{ textDecoration: isMoratorium ? 'line-through' : 'none' }}
                                                        >
                                                            {isMoratorium ? '-' : formatCurrency(item.emiAmount)}
                                                        </Typography>
                                                        {/* ✅ Show accumulated interest from backend */}
                                                        {/* {item.includesOverdueInterest && !isMoratorium && (
                                                            <Typography variant="caption" color="error.main">
                                                                + {formatCurrency(item.accumulatedInterest || 0)} overdue interest
                                                            </Typography>
                                                        )} */}
                                                    </Box>
                                                </TableCell>

                                                {/* Principal */}
                                                <TableCell>
                                                    {isMoratorium ? '-' : formatCurrency(
                                                        // ✅ Use backend-calculated repaidPrincipal, fallback to original principal
                                                        item.repaidPrincipal > 0 ? item.repaidPrincipal : item.principal
                                                    )}
                                                </TableCell>

                                                {/* Interest - Use backend totalInterestDisplay */}
                                                <TableCell>
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            sx={{ textDecoration: isMoratorium ? 'line-through' : 'none' }}
                                                        >
                                                            {isMoratorium ? '-' : formatCurrency(item.totalInterestDisplay || item.interest)}
                                                        </Typography>
                                                        
                                                        {item.includesOverdueInterest && !isMoratorium && (
                                                            <Typography variant="caption" color="error.main">
                                                                (includes {formatCurrency(item.accumulatedInterest || 0)} overdue)
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                {/* Balance */}
                                                <TableCell>
                                                    {formatCurrency(item.balance)}
                                                </TableCell>

                                                {/* Repaid Amount - EMI portion */}
                                                <TableCell>
                                                    {isMoratorium ? '-' : formatCurrency(item.repaidAmount)}
                                                </TableCell>

                                                {/* Actual Paid - Total payment received */}
                                                {/* <TableCell>
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            sx={{ textDecoration: isMoratorium ? 'line-through' : 'none' }}
                                                        >
                                                            {isMoratorium ? '-' : formatCurrency(item.repaidAmount)}
                                                        </Typography>
                                                        
                                                        {!isMoratorium && item.repaidAmount > item.emiAmount && (
                                                            <Typography variant="caption" color="success.main">
                                                                (+{formatCurrency(item.repaidAmount - item.emiAmount)} prepay)
                                                            </Typography>
                                                        )}
                                                        
                                                        {!isMoratorium && item.includesOverdueInterest && item.repaidAmount > 0 && (
                                                            <Typography variant="caption" color="warning.main">
                                                                Paid {formatCurrency(item.accumulatedInterest || 0)} overdue interest
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell> */}

                                                {/* Status */}
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <StatusChip status={item.status} />
                                                        {/* ✅ Show overdue indicator from backend */}
                                                        {!isMoratorium && item.includesOverdueInterest && (
                                                            <Tooltip title={`Includes ${formatCurrency(item.accumulatedInterest || 0)} overdue interest from previous months`}>
                                                                <Warning color="warning" fontSize="small" />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                {/* Action */}
                                                <TableCell>
                                                    {!['Paid', 'Overdue', 'Moratorium'].includes(item.status) && !isMoratorium && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            sx={{
                                                                backgroundColor: "#4DD26B",
                                                                textTransform: "none",
                                                                "&:hover": { backgroundColor: "#3BB85A" }
                                                            }}
                                                            onClick={() => handleRepaymentClick(item)}
                                                            disabled={loanData.status !== 'disbursed' || userType !== 'block'}
                                                            startIcon={<PaymentIcon />}
                                                        >
                                                            Pay
                                                        </Button>
                                                    )}
                                                    {isMoratorium && (
                                                        <Chip
                                                            label="No Payment"
                                                            size="small"
                                                            color="default"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    ) : (
                        /* Mobile Cards View */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                            {emiData.schedule
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((item: EmiItem, idx: number) => {
                                    const today = new Date();
                                    const isCurrent = new Date(item.dueDate).getMonth() === today.getMonth() &&
                                        new Date(item.dueDate).getFullYear() === today.getFullYear();

                                    return (
                                        <Paper
                                            key={idx}
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                backgroundColor: isCurrent ? 'primary.light' : 'background.paper',
                                                borderLeft: isCurrent ? `4px solid ${theme.palette.primary.main}` : undefined
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Month {item.month}
                                                </Typography>
                                                <StatusChip status={item.status} />
                                            </Box>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Due Date:</Typography>
                                                    <Typography variant="body2">{new Date(item.dueDate).toLocaleDateString()}</Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">EMI Amount:</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {formatCurrency(item.emiAmount)}
                                                    </Typography>
                                                </Box>

                                                {/* Show actual payment if exists */}
                                                {item.actualPayment && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" color="text.secondary">Actual Paid:</Typography>
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                                                                {formatCurrency(item.actualPayment.amount)}
                                                            </Typography>
                                                            {item.actualPayment.prepaymentPortion > 0 && (
                                                                <Typography variant="caption" color="success.main">
                                                                    (+{formatCurrency(item.actualPayment.prepaymentPortion)} prepayment)
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Repaid (EMI):</Typography>
                                                    <Typography variant="body2">
                                                        {formatCurrency(item.repaidAmount)}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">Balance:</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {formatCurrency(item.balance)}
                                                    </Typography>
                                                </Box>

                                                {/* Show explanation if available */}
                                                {item.explanation && (
                                                    <Box sx={{
                                                        backgroundColor: 'success.light',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        mt: 1
                                                    }}>
                                                        <Typography variant="caption" color="success.dark">
                                                            {item.explanation}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                                {item.status !== 'Paid' && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: "#4DD26B",
                                                            textTransform: "none",
                                                            "&:hover": { backgroundColor: "#3BB85A" }
                                                        }}
                                                        onClick={() => handleRepaymentClick(item)}
                                                        disabled={loanData.status !== 'disbursed' || userType !== 'block'}
                                                        startIcon={<PaymentIcon />}
                                                    >
                                                        Pay
                                                    </Button>
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })}
                        </Box>
                    )}

                    {/* Desktop Pagination */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#4DD26B",
                                        textTransform: "none",
                                        "&:hover": { backgroundColor: "#3BB85A" }
                                    }}
                                    onClick={() => handleDownload("pdf")}
                                    startIcon={<DownloadIcon />}
                                >
                                    PDF
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="success"
                                    onClick={() => handleDownload("excel")}
                                    startIcon={<DownloadIcon />}
                                >
                                    Excel
                                </Button>
                            </Box>

                            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ mr: 1 }}>
                                        Rows per page:
                                    </Typography>
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        style={{
                                            padding: '8px',
                                            borderRadius: '4px',
                                            border: `1px solid ${theme.palette.divider}`
                                        }}
                                    >
                                        {[5, 10, 25, 50].map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{
                    textAlign: 'center',
                    py: 4,
                    backgroundColor: 'grey.50',
                    borderRadius: 2,
                    mt: 3
                }}>
                    <Typography variant="body1" color="text.secondary">
                        Waiting for EMI schedule to be generated
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        The EMI schedule will appear here once calculated
                    </Typography>
                </Box>
            )}
        </Paper>
    )
}

export default EmiSchedules